import mapboxgl from 'mapbox-gl';
import {uniqueId, map, mapValues, merge, omit} from 'lodash';
import {useState} from 'react';
import savePoint, {registerSavedPoint} from '../utils/gaia/savePoint';
import trackAnalytics from '../utils/gaia/trackAnalytics';
import useIsSaved from './useIsSaved';
import {formatCoordinates} from '../utils/formatters';
import convertLngLat from '../utils/mapbox/convertLngLat';
import {TrackDispatcher} from './useTracks';
import fetchJSON from '../utils/fetchJSON';

interface CreateAction {
    type: 'create';
    lngLat: mapboxgl.LngLatLike;
    title?: string;
    isTitleLoaded?: boolean;
}
interface UpdateAction {type: 'update'; waypoint: Waypoint | TrackPoint; save: boolean }
interface TakeLocalAction {
    type: 'takeLocal';
    waypoint: {
        type?: 'Feature';
        properties: Partial<Waypoint['properties']>;
        geometry?: Waypoint['geometry'];
    };
    callback?: (waypoint: Waypoint) => void;
}
interface RemoveAction {type: 'remove'; waypoint: Waypoint | TrackPoint}
interface UpdatePropertiesAction {
    type: 'updateProperties';
    properties: Partial<Waypoint['properties']> & {pointLocalId: string};
    save: boolean;
}
type Action = (
    CreateAction |
    UpdateAction |
    TakeLocalAction |
    RemoveAction |
    UpdatePropertiesAction
);

export type WaypointDispatcher = (action: Action) => string;

export default function useWaypoints(
    tracks: Record<string, Track>,
    trackDispatcher: TrackDispatcher,
): [{[pointLocalId: string]: Waypoint | TrackPoint}, WaypointDispatcher, boolean] {

    const [waypoints, setWaypoints] = useState<{[pointLocalId: string]: Waypoint}>({});
    const [isSaved, addSavePromise] = useIsSaved();

    const trackPoints = merge({}, ...map(tracks, track => {
        return track.isGeometryLoaded && track.points;
    }));

    function create(action: CreateAction) {
        trackAnalytics('Mark Waypoint', {category: 'map2'});
        const lngLat = convertLngLat(action.lngLat);

        const waypoint: Waypoint = {
            type: 'Feature',
            properties: {
                title: action.title || formatCoordinates(lngLat.toArray()),
                writable: true,
                pointLocalId: uniqueId(),
                isLocallyDeleted: false,
                icon: 'red-pin-down.png',
                isTitleLoaded: Boolean(action.isTitleLoaded),
                isLocallyCreated: true,
                time_created: (new Date()).toISOString(),
            },
            geometry: {
                type: 'Point',
                coordinates: lngLat.toArray()
            }
        };

        setWaypoints({...waypoints, [waypoint.properties.pointLocalId]: waypoint});
        savePoint(waypoint, addSavePromise);
        return waypoint.properties.pointLocalId;
    }

    function update(action: UpdateAction) {
        if (isTrackPoint(action.waypoint)) {
            return trackDispatcher({
                type: 'updatePoint',
                point: action.waypoint,
                save: action.save
            });
        }

        const {waypoint} = action;
        setWaypoints({
            ...waypoints,
            [waypoint.properties.pointLocalId]: waypoint
        });
        if (action.save) savePoint(waypoint, addSavePromise);

        return waypoint.properties.pointLocalId;
    }

    function updateProperties({properties, save}: UpdatePropertiesAction) {
        const waypoint = (
            waypoints[properties.pointLocalId] ||
            trackPoints[properties.pointLocalId]
        );
        return update({
            type: 'update',
            save,
            waypoint: {
                ...waypoint,
                properties: {...waypoint.properties, ...properties}
            },
        });
    }

    function takeLocal(action: TakeLocalAction) {

        const pointLocalId = (
            action.waypoint.properties.id ||
            action.waypoint.properties.pointLocalId
        )!;

        if (waypoints[pointLocalId]) {
            if (action.callback) action.callback(waypoints[pointLocalId]);
            return pointLocalId;

        } else if (pointLocalId && action.waypoint.properties.track_id) {
            trackDispatcher({
                type: 'takeLocal',
                track: {
                    properties: {
                        id: action.waypoint.properties.track_id,
                        isPublicTrack: false
                    }
                },
            });
            return pointLocalId;

        } else {
            const waypoint1: OnePartial<Waypoint, 'geometry'> = {
                type: 'Feature',
                properties: {
                    ...mapValues(omit(action.waypoint.properties, 'photos'), (value: any) => {
                        if (value === 'null') return null;
                        else return value;
                    }) as RemoteWaypoint['properties'],
                    pointLocalId,
                    isLocallyDeleted: false,
                    isTitleLoaded: true,
                    isLocallyCreated: false,
                },
                geometry: action.waypoint.geometry
            };

            if (waypoint1.geometry) {
                registerSavedPoint(waypoint1 as Waypoint);
                setWaypoints({
                    ...waypoints,
                    [pointLocalId]: waypoint1 as Waypoint
                });
            }

            fetchJSON(`/api/objects/waypoint/${waypoint1.properties.id}`, {
                method: 'GET',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},

            }).then((waypoint2: RemoteWaypoint) => {
                setWaypoints(latestWaypoints => {
                    const latestWaypoint: Waypoint = {
                        type: 'Feature',
                        geometry: waypoint2.geometry,
                        properties: {
                            ...waypoint1.properties,
                            ...waypoint2.properties,
                            ...latestWaypoints[pointLocalId]?.properties
                        }
                    };

                    registerSavedPoint(latestWaypoint);
                    if (action.callback) action.callback(latestWaypoint);

                    return {
                        ...latestWaypoints,
                        [pointLocalId]: latestWaypoint
                    };
                });
            });

            return pointLocalId;
        }
    }

    function remove(action: RemoveAction) {
        if (isTrackPoint(action.waypoint)) {
            return trackDispatcher({
                type: 'removePoint',
                pointLocalId: action.waypoint.properties.pointLocalId,
                trackLocalId: action.waypoint.properties.trackLocalId,
            });
        }

        const waypoint = {
            ...action.waypoint,
            properties: {
                ...action.waypoint.properties,
                isLocallyDeleted: true,
            }
        };
        setWaypoints({...waypoints, [waypoint.properties.pointLocalId]: waypoint});
        savePoint(waypoint, addSavePromise);
        return waypoint.properties.pointLocalId;
    }

    function dispatcher(action: Action): string {
        if (action.type === 'create') return create(action);
        else if (action.type === 'update') return update(action);
        else if (action.type === 'takeLocal') return takeLocal(action);
        else if (action.type === 'remove') return  remove(action);
        else if (action.type === 'updateProperties') return updateProperties(action);
        else throw new Error('Unknown action type');
    }

    return [{...waypoints, ...trackPoints}, dispatcher, isSaved];
}

function isTrackPoint(point: Waypoint | TrackPoint): point is TrackPoint {
    return Boolean(point.properties.trackLocalId);
}
